import { Inject, Injectable, Optional } from '@angular/core';
import { ParamMap } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { LocaleStartParams } from '@platform-frontend/transloco';
import { NavigateByUrl, SimpleRouterNavigationExtras, SimpleRouterState } from '@platform-frontend/util';
import { Buffer } from 'buffer';
import { Observable } from 'rxjs';
import { AccountDisabledServiceToken, AuthenticationServiceToken, IAccountDisabledService, IAuthenticationService } from '../account-services';
import { AccountNavigationExtras } from './model/account-navigation-extras';
import { AccountStartParams } from './model/account-start-params';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class AccountRouteService {
  constructor(
    @Inject(AccountDisabledServiceToken) @Optional() private accountDisabledService: IAccountDisabledService | null,
    @Inject(AuthenticationServiceToken) @Optional() private authenticationService: IAuthenticationService | null,
    private store: Store
  ) {}

  navigateToRedirectUrl(noRedirectCallback: () => Observable<unknown>, extras?: SimpleRouterNavigationExtras): Observable<unknown> {
    const redirectUrl = this.getRedirectUrl(this.store.selectSnapshot(SimpleRouterState.paramMap));
    return redirectUrl ? this.store.dispatch(new NavigateByUrl(redirectUrl, extras)) : noRedirectCallback();
  }

  getRedirectUrl(paramMap: ParamMap): string | undefined {
    return this.getStartParams(paramMap).redirect;
  }

  extendAccountNavigationExtras<T extends AccountNavigationExtras>(route: string, paramMap: ParamMap, queryParamMap: ParamMap, extras: T): T {
    return this.isAccountRoute(route)
      ? this.mergeStartParams(this.getStartParams(paramMap), extras)
      : this.mergeStartParams(
          {
            redirect: this.authenticationService ? this.authenticationService.adjustRedirectUrl(route) : route, // TODO (MP-2518): Remove the adjustRedirectUrl workaround.
            appStartParams: this.authenticationService?.getAppStartParams(queryParamMap)
          },
          extras
        );
  }

  private isAccountRoute(route: string): boolean {
    if (this.accountDisabledService && this.authenticationService) {
      return route.startsWith(this.authenticationService.getRouteParent()) || route.startsWith(this.accountDisabledService.getRouteParent());
    } else {
      return false;
    }
  }

  private mergeStartParams<T extends AccountNavigationExtras>(startParams: AccountStartParams, extras: T): T {
    return {
      ...startParams,
      ...extras,
      appStartParams: {
        ...startParams.appStartParams,
        ...extras.appStartParams
      }
    };
  }

  getStartParams<T extends LocaleStartParams = LocaleStartParams>(paramMap: ParamMap): AccountStartParams<T> {
    const startParamsBase64 = paramMap.get('startParams');
    return startParamsBase64 ? (JSON.parse(Buffer.from(startParamsBase64, 'base64').toString()) as AccountStartParams<T>) : {};
  }
}
